import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import { applyLocaleFromUser, clearLocale, getCurrentLocale } from "../utils/locale";

// Create the API instance with the correct backend URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include the auth token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    const locale = getCurrentLocale();
    if (locale) {
      config.headers["Accept-Language"] = locale;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Reflect the server-resolved language (user > team > org > browser) on
  // <html lang/dir> and in storage so API calls carry it as Accept-Language.
  useEffect(() => {
    applyLocaleFromUser(user);
  }, [user?.effective_locale, user?.effective_locale_rtl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user is logged in on initial load
  useEffect(() => {
    const loadUser = async () => {
      try {
        // Get token from local storage
        const token = localStorage.getItem("token");
        
        if (!token) {
          setLoading(false);
          setIsInitialized(true);
          return;
        }
        
        // If token requires a password change, don't restore session —
        // let ProtectedRoute redirect to login where the set-password step will show
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          if (payload.must_change_password) {
            setLoading(false);
            setIsInitialized(true);
            return;
          }
        } catch {
          // ignore decode errors
        }

        // Verify token and get user data
        const response = await api.get("/api/v1/users/me");

        if (response.data) {
          console.log('Current user from /users/me:', response.data); // Debug log
          localStorage.setItem('userId', response.data.id); // Store user ID for easier access
          setUser(response.data);
        }
      } catch (err) {
        // If token is invalid, clear it
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        setUser(null);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    loadUser();
  }, []);

  // Login function
  // expectedOrgId, when provided (typically from a tenant subdomain), is sent
  // as an extra OAuth2 form field. The backend rejects logins from users who
  // belong to a different organization.
  const login = async (email, password, rememberMe = false, expectedOrgId = null) => {
    if (loading) return; // Prevent login attempts while still loading

    try {
      setLoading(true);
      setError(null);

      // Make API request to login (FastAPI OAuth2 format requires form-data)
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      if (expectedOrgId) {
        formData.append('expected_org_id', expectedOrgId);
      }

      const response = await api.post("/api/v1/login/access-token", formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      

      // Check if login was successful (FastAPI returns access_token)
      if (response.data && response.data.access_token) {
        // Store token in localStorage or sessionStorage based on rememberMe
        if (rememberMe) {
          localStorage.setItem("token", response.data.access_token);
        } else {
          sessionStorage.setItem("token", response.data.access_token);
          // Also set in localStorage so our interceptor can find it
          localStorage.setItem("token", response.data.access_token);
        }

        // Check if the token requires a password change — if so, don't
        // set user state (which would trigger a redirect away from login)
        try {
          const payload = JSON.parse(atob(response.data.access_token.split(".")[1]));
          if (payload.must_change_password) {
            setLoading(false);
            return { must_change_password: true };
          }
        } catch {
          // Token decode failed — continue normally
        }

        // After getting the token, fetch the user data
        try {
          const userResponse = await api.get("/api/v1/users/me");

          if (userResponse.data) {
            console.log('User after login:', userResponse.data); // Debug log
            localStorage.setItem('userId', userResponse.data.id); // Store user ID
            setUser(userResponse.data);
            setLoading(false);
            return userResponse.data;
          }
        } catch (userErr) {
          setLoading(false);
          throw userErr;
        }
      } else {
        setLoading(false);
        throw new Error("Login failed: No token received");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Login failed. Please try again.");
      setLoading(false);
      throw err;
    }
  };

  // Refresh user data from the server
  const refreshUser = async () => {
    try {
      const response = await api.get("/api/v1/users/me");
      if (response.data) {
        localStorage.setItem('userId', response.data.id);
        setUser(response.data);
      }
    } catch {
      // If refresh fails, leave current state
    }
  };

  // Logout function
  const logout = () => {
    // Clear token from storage
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    clearLocale();

    // Clear user data
    setUser(null);
  };

  // Register function
  const register = async (name, email, password) => {
    if (loading) return; // Prevent registration attempts while still loading
    
    try {
      setLoading(true);
      setError(null);
      
      // Make API request to register
      const response = await api.post("/auth/register", {
        name,
        email,
        password,
      });
      
      // Check if registration was successful
      if (response.data && response.data.token) {
        // Store token
        localStorage.setItem("token", response.data.token);
        
        // After getting the token, fetch the user data
        try {
          const userResponse = await api.get("/api/v1/auth/me");
          if (userResponse.data && userResponse.data.user) {
            setUser(userResponse.data.user);
            setLoading(false);
            return userResponse.data.user;
          }
        } catch (userErr) {
          setLoading(false);
          throw userErr;
        }
      } else {
        setLoading(false);
        throw new Error("Registration failed: No token received");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Registration failed. Please try again.");
      setLoading(false);
      throw err;
    }
  };

  // SSO login: exchange a one-time SSO code for an access token
  const loginWithSSOCode = async (code) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post("/api/v1/sso/token-exchange", { code });

      if (response.data && response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);

        const userResponse = await api.get("/api/v1/users/me");
        if (userResponse.data) {
          localStorage.setItem("userId", userResponse.data.id);
          setUser(userResponse.data);
          setLoading(false);
          return userResponse.data;
        }
      } else {
        setLoading(false);
        throw new Error("SSO login failed: No token received");
      }
    } catch (err) {
      setError("SSO login failed. Please try again.");
      setLoading(false);
      throw err;
    }
  };

  // Check if token is expired (utility for future use)
  // eslint-disable-next-line no-unused-vars
  const isTokenExpired = (token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp < Date.now() / 1000;
    } catch (err) {
      return true; // If we can't decode the token, assume it's expired
    }
  };

  // Value object for the context provider
  const value = {
    user,
    loading,
    error,
    login,
    loginWithSSOCode,
    logout,
    register,
    refreshUser,
    isAuthenticated: !!user,
    isInitialized,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};